import { Stack } from "@mui/material";
import { MprcButton } from "components";
import { MprcButtonProps } from "components/MprcButton";
import { EnrollmentViewDto, TrainingAssessmentItemDto } from "generated/CoreApi_v1";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { AssPlayerEvaluation } from "views/assessment/player/AssPlayerEvaluation";
import { useEnrollmentViewContext } from "../../..";
import { getEnrollmentCertificate, ReEnrollAction } from "../../../Shared/EnrollmentActions";
import { EnrollmentCloseButton } from "../../../Shared/EnrollmentCloseButton";

interface IProps {
    assessment: TrainingAssessmentItemDto;
    enrollmentId: string;
    componentId: string;
    enrollment?: EnrollmentViewDto;
    onRefresh: () => void;
}

export const ActionButtonAssessment = (props: IProps) => {

    const { assessment, componentId, onRefresh } = props;

    const { enrollmentId, enrollment, onReload } = useEnrollmentViewContext();

    const [ulrSearchParams] = useSearchParams();
    const { t } = useTranslation("lms");

    const [dialog, setDialog] = useState<any>();

    /////////////////////////////////////////////////////////////////////////////////////

    const singleComponent = enrollment.totalComponents == 1;
    const minFinalScore = assessment.minFinalScore > 0 && assessment.onlyComponentFinalScore ? assessment.weight : undefined;
    const hasCertificate = (enrollment.hasCertificate && enrollment.certificateId != null);
    const hasSecondaryAction = singleComponent && (hasCertificate || (enrollment.finalizationDate == null && enrollment.canFinish) || enrollment.canReEnroll);

    ///
    const ExecuteInstanceRender = () => {
        let instanceId = !assessment.canAnswer || (assessment.allowContinueAbortedEvaluation && assessment.lastInstanceId != null && assessment.completedOn == null)
            ? assessment.lastInstanceId : undefined;

        return (
            <AssPlayerEvaluation
                instanceId={instanceId}
                assessmentId={assessment.assessmentId}
                enrollmentId={!enrollment.isPreview ? enrollmentId : null}
                componentId={componentId}
                minFinalScore={minFinalScore}
                preview={enrollment.isPreview}
                onClose={() => {
                    setDialog(null);
                    onRefresh();
                    onReload();
                }}
            />
        );
    };

    const executeAssInstance = () => {
        if (enrollment.finalizationDate != null && assessment.tries == 0) {
            enqueueSnackbar(t("Student.EnrollmentView.Content.Assessment.NotAnsweredMessage"), { variant: "warning" });
            return;
        }

        setDialog(ExecuteInstanceRender);
    };

    const AnswerButton = useMemo(() => {

        var answerButtonProps: MprcButtonProps | null = null;

        if (assessment.canAnswer) {
            if (assessment.tries == 0) {
                //Responder
                answerButtonProps = {
                    text: t(hasSecondaryAction
                        ? "Student.EnrollmentView.Content.Assessment.AnswerButtonShortText"
                        : "Student.EnrollmentView.Content.Assessment.AnswerButtonText"
                    ),
                    title: t("Student.EnrollmentView.Content.Assessment.AnswerButtonTitle"),
                    color: "warning",
                    fullWidth: true,
                };
            }
            else {
                //Responder novamente
                answerButtonProps = {
                    text: t(hasSecondaryAction
                        ? "Student.EnrollmentView.Content.Assessment.AnswerButtonShortText"
                        : "Student.EnrollmentView.Content.Assessment.AnswerAgainButtonText"
                    ),
                    title: t("Student.EnrollmentView.Content.Assessment.AnswerAgainButtonTitle"),
                    color: "warning",
                    fullWidth: true
                };
            }
        }
        else {
            //Rever avaliação
            answerButtonProps = {
                text: t(hasSecondaryAction
                    ? "Student.EnrollmentView.Content.Assessment.ReviewButtonShortText"
                    : "Student.EnrollmentView.Content.Assessment.ReviewButtonText"
                ),
                title: t("Student.EnrollmentView.Content.Assessment.ReviewButtonTitle"),
                color: "primary",
                fullWidth: !hasSecondaryAction
            };
        }

        return <MprcButton
            {...answerButtonProps}
            variant="contained"
            onClick={executeAssInstance}
        />;
    }, [assessment, hasSecondaryAction]);

    ///
    const SecondaryActionButton = useMemo(() => {

        var secondaryActionProps: MprcButtonProps | null = null;

        if (!singleComponent) return null;

        if (enrollment.canReEnroll) {
            //Reinscrição
            secondaryActionProps = {
                text: t("Student.EnrollmentView.ReEnrollButtonText"),
                title: t("Student.EnrollmentView.ReEnrollButtonText"),
                sx: { p: 0 },
                onClick: () => setDialog(<ReEnrollAction onClose={() => setDialog(null)} />)
            };
        }
        else if (enrollment.canFinish) {
            return <EnrollmentCloseButton />;
        }
        else if (hasCertificate) {
            //Certificado
            secondaryActionProps = {
                text: t("Student.EnrollmentView.Content.Assessment.CertificateButtonText"),
                title: t("Student.EnrollmentView.Content.Assessment.CertificateButtonTitle"),
                onClick: () => getEnrollmentCertificate(enrollment.certificateId!)
            };
        }

        return secondaryActionProps != null ?
            <MprcButton
                {...secondaryActionProps}
                variant="contained"
                fullWidth
            />
            : null;

    }, [hasCertificate, enrollment]);

    useEffect(() => {
        if (ulrSearchParams.has("oos") && assessment.canAnswer) setDialog(ExecuteInstanceRender);
    }, []);

    /////////////////////////////////////////////////////////////////////////////////////
    /// RENDER
    /////////////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {dialog}
            <Stack direction="row" spacing={1}>
                {hasSecondaryAction && SecondaryActionButton}
                {AnswerButton}
            </Stack >
        </>
    );
};
